/**
 * Phase 10: Community & Precedent Data Panel
 *
 * Standalone full-page component accessible at /community.
 * Shows:
 *  - Free tier (all authenticated users):
 *    - Approval rates by pathway
 *    - Average processing times
 *    - Top 5 RFE reasons per pathway
 *    - Recent USCIS policy changes (90-day rolling)
 *  - Full tier (any active paid entitlement):
 *    - Real-time pathway stats with service center and country filters
 *    - Evidence category frequency breakdown
 *    - 12-month approval rate trend chart
 *    - Country of birth breakdown (EB pathways)
 *  - Policy alerts (user-specific, with mark-as-read)
 *  - Opt-out toggle for data contribution
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LAUNCH } from '../lib/launch';
import { ComingSoonBanner } from './ComingSoon';
import { PurchaseNavButton } from './CheckoutFlow';
import { API_BASE as API } from '../lib/apiBase';

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.detail?.message || data?.detail || 'Request failed';
    throw Object.assign(new Error(msg), { status: res.status, data });
  }
  return data;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PATHWAY_LABELS = {
  'eb1a':             'EB-1A Extraordinary Ability',
  'eb2-niw':          'EB-2 NIW',
  'perm-employer':    'PERM / Employer-Sponsored',
  'family-i130':      'Family-Based I-130',
  'aos-i485':         'Adjustment of Status',
  'asylum':           'Asylum & Humanitarian',
  'f1-change-status': 'F-1 / Change of Status',
  'o1-nonimmigrant':  'O-1 Nonimmigrant',
  'dv-lottery':       'Diversity Visa (DV Lottery)',
};

const SHORT_LABELS = {
  'eb1a':             'EB-1A',
  'eb2-niw':          'EB-2 NIW',
  'perm-employer':    'PERM',
  'family-i130':      'Family I-130',
  'aos-i485':         'AOS',
  'asylum':           'Asylum',
  'f1-change-status': 'F-1',
  'o1-nonimmigrant':  'O-1',
  'dv-lottery':       'DV Lottery',
};

const SERVICE_CENTERS = ['Nebraska', 'Texas', 'California', 'Vermont'];
const EB_PATHWAYS = new Set(['eb2-niw', 'eb1a', 'perm-employer', 'o1-nonimmigrant']);
const ALL_PATHWAYS = Object.keys(PATHWAY_LABELS);

const fmtPct = (val) => (val == null ? '—' : `${Math.round(val * 100)}%`);
const fmtDays = (val) => {
  if (val == null) return '—';
  const d = Math.round(val);
  if (d >= 365) return `~${(d / 365).toFixed(1)} yrs`;
  if (d >= 30) return `~${Math.round(d / 30)} mo`;
  return `${d}d`;
};

// ---------------------------------------------------------------------------
// Trend bar chart
// ---------------------------------------------------------------------------
const ApprovalTrendChart = ({ trend }) => {
  if (!trend || trend.length === 0) {
    return <p className="text-gray-500 text-sm italic">No trend data available.</p>;
  }
  const maxCount = Math.max(...trend.map((t) => t.count), 1);
  return (
    <div className="space-y-1.5">
      {trend.map((point, idx) => {
        const pct = Math.max(6, Math.round((point.count / maxCount) * 100));
        const approvalPct = Math.round((point.approval_rate || 0) * 100);
        return (
          <div key={idx} className="flex items-center gap-2 text-xs">
            <span className="text-gray-500 w-14 shrink-0 text-right">{point.month}</span>
            <div className="flex-1 bg-gray-800 rounded h-5 relative overflow-hidden">
              <div
                className="h-full rounded bg-gold/80 transition-all"
                style={{ width: `${pct}%` }}
              />
              <span className="absolute inset-0 flex items-center px-2 text-white text-xs font-medium">
                {approvalPct}% approval ({point.count} cases)
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Approval rate card for a single pathway (free tier)
// ---------------------------------------------------------------------------
const PathwayCard = ({ pathwayId, approvalRate, avgDays, recordCount, insufficient }) => (
  <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
    <h4 className="font-semibold text-white text-sm mb-2">
      {SHORT_LABELS[pathwayId] || pathwayId}
    </h4>
    {insufficient ? (
      <p className="text-gray-500 text-xs italic">Insufficient data (&lt;50 cases)</p>
    ) : (
      <>
        <div className="text-2xl font-bold text-green-400">{fmtPct(approvalRate)}</div>
        <div className="text-xs text-gray-500 mt-1">Approval rate</div>
        <div className="text-sm text-gray-400 mt-2">
          Avg processing: <strong className="text-white">{fmtDays(avgDays)}</strong>
        </div>
        <div className="text-xs text-gray-600 mt-1">{recordCount} cases on file</div>
      </>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// RFE reasons accordion
// ---------------------------------------------------------------------------
const RFEReasonsSection = ({ topRfeReasons }) => {
  const [expanded, setExpanded] = useState(null);
  const pathways = Object.keys(topRfeReasons || {});
  if (pathways.length === 0) return <p className="text-gray-500 text-sm italic">No RFE data yet.</p>;
  return (
    <div className="space-y-2">
      {pathways.map((pid) => (
        <div key={pid} className="border border-gray-700 rounded-lg overflow-hidden">
          <button
            onClick={() => setExpanded(expanded === pid ? null : pid)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-900 hover:bg-gray-800 text-left text-sm font-medium text-white"
          >
            <span>{SHORT_LABELS[pid] || pid}</span>
            <i className={`fas fa-chevron-${expanded === pid ? 'up' : 'down'} text-gray-500 text-xs`} />
          </button>
          {expanded === pid && (
            <ol className="px-4 py-3 bg-gray-900/50 space-y-2">
              {(topRfeReasons[pid] || []).map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                  <span className="text-gold font-bold shrink-0">{r.rank}.</span>
                  <span>{r.reason}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Policy change alert card
// ---------------------------------------------------------------------------
const AlertTypeIcon = ({ type }) => {
  const icons = {
    policy_update: { icon: 'fa-file-alt', color: 'text-blue-400', label: 'Policy Update' },
    precedent_decision: { icon: 'fa-gavel', color: 'text-purple-400', label: 'Precedent Decision' },
    processing_bulletin: { icon: 'fa-clock', color: 'text-orange-400', label: 'Processing Bulletin' },
  };
  const { icon, color, label } = icons[type] || { icon: 'fa-info-circle', color: 'text-gray-500', label: type };
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`}>
      <i className={`fas ${icon}`} /> {label}
    </span>
  );
};

const PolicyAlertCard = ({ alert, onMarkRead }) => (
  <div className={`border rounded-lg p-4 ${alert.read ? 'border-gray-700 bg-gray-900' : 'border-gold/40 bg-gray-900'}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <AlertTypeIcon type={alert.alert_type} />
          <span className="text-xs text-gray-500">{alert.days_ago === 0 ? 'Today' : `${alert.days_ago}d ago`}</span>
          <span className="text-xs text-gray-600">·</span>
          <span className="text-xs text-gray-500">{SHORT_LABELS[alert.pathway_id] || alert.pathway_id}</span>
        </div>
        <h4 className="font-semibold text-white text-sm">{alert.title}</h4>
        <p className="text-gray-400 text-sm mt-1">{alert.summary}</p>
        {alert.recommended_action && (
          <p className="text-gold text-sm mt-2">
            <i className="fas fa-lightbulb mr-1" />
            {alert.recommended_action}
          </p>
        )}
      </div>
      {!alert.read && (
        <button
          onClick={() => onMarkRead(alert.id)}
          className="shrink-0 text-xs text-gold hover:text-gold/80 underline"
        >
          Mark read
        </button>
      )}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function CommunityDataPanel() {
  const navigate = useNavigate();

  // Tier & entitlement
  const [isFullTier, setIsFullTier] = useState(false);
  const [activePathways, setActivePathways] = useState([]);

  // Free tier data
  const [freeTierData, setFreeTierData] = useState(null);
  const [loadingFree, setLoadingFree] = useState(true);
  const [errorFree, setErrorFree] = useState('');

  // Full tier data
  const [selectedPathway, setSelectedPathway] = useState('');
  const [selectedSC, setSelectedSC] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [fullTierData, setFullTierData] = useState(null);
  const [loadingFull, setLoadingFull] = useState(false);
  const [errorFull, setErrorFull] = useState('');

  // Policy alerts
  const [policyAlerts, setPolicyAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingAlerts, setLoadingAlerts] = useState(true);

  // Opt-out
  const [optOut, setOptOut] = useState(false);
  const [savingOptOut, setSavingOptOut] = useState(false);

  // Active tabs for RFE + policy sections
  const [activeTab, setActiveTab] = useState('approvals');

  // ---------------------------------------------------------------------------
  // Fetch entitlement status on mount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    apiFetch('/api/me/entitlement')
      .then((data) => {
        const pathwayIds = data.active_pathway_ids || [];
        setIsFullTier(pathwayIds.length > 0);
        setActivePathways(pathwayIds);
        if (pathwayIds.length > 0) setSelectedPathway(pathwayIds[0]);
      })
      .catch(() => {
        setIsFullTier(false);
      });
  }, []);

  // ---------------------------------------------------------------------------
  // Fetch free tier stats
  // ---------------------------------------------------------------------------
  const fetchFreeTier = useCallback(async () => {
    setLoadingFree(true);
    setErrorFree('');
    try {
      const data = await apiFetch('/api/community/free-tier');
      setFreeTierData(data);
    } catch (err) {
      setErrorFree(err.message || 'Failed to load community stats');
    } finally {
      setLoadingFree(false);
    }
  }, []);

  useEffect(() => {
    fetchFreeTier();
  }, [fetchFreeTier]);

  // ---------------------------------------------------------------------------
  // Fetch full tier stats
  // ---------------------------------------------------------------------------
  const fetchFullTier = useCallback(async () => {
    if (!isFullTier || !selectedPathway) return;
    setLoadingFull(true);
    setErrorFull('');
    try {
      const params = new URLSearchParams();
      if (selectedSC) params.append('service_center', selectedSC);
      if (selectedCountry && EB_PATHWAYS.has(selectedPathway)) params.append('country_of_birth', selectedCountry);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const data = await apiFetch(`/api/community/${selectedPathway}${qs}`);
      setFullTierData(data);
    } catch (err) {
      setErrorFull(err.message || 'Failed to load detailed stats');
    } finally {
      setLoadingFull(false);
    }
  }, [isFullTier, selectedPathway, selectedSC, selectedCountry]);

  useEffect(() => {
    if (isFullTier) fetchFullTier();
  }, [isFullTier, fetchFullTier]);

  // ---------------------------------------------------------------------------
  // Fetch policy alerts
  // ---------------------------------------------------------------------------
  const fetchAlerts = useCallback(async () => {
    setLoadingAlerts(true);
    try {
      const data = await apiFetch('/api/community/policy-alerts');
      setPolicyAlerts(data.alerts || []);
      setUnreadCount(data.unread_count || 0);
    } catch {
      // Non-fatal
    } finally {
      setLoadingAlerts(false);
    }
  }, []);

  useEffect(() => {
    if (LAUNCH.showPolicyAlerts) fetchAlerts();
  }, [fetchAlerts]);

  // ---------------------------------------------------------------------------
  // Mark alert read
  // ---------------------------------------------------------------------------
  const handleMarkRead = async (alertId) => {
    try {
      await apiFetch(`/api/community/policy-alerts/${alertId}/read`, { method: 'PATCH' });
      setPolicyAlerts((prev) =>
        prev.map((a) => (a.id === alertId ? { ...a, read: true } : a))
      );
      setUnreadCount((n) => Math.max(0, n - 1));
    } catch {
      // Non-fatal
    }
  };

  // ---------------------------------------------------------------------------
  // Opt-out toggle
  // ---------------------------------------------------------------------------
  const handleOptOutToggle = async () => {
    const newVal = !optOut;
    setSavingOptOut(true);
    try {
      await apiFetch('/api/user/settings', {
        method: 'PATCH',
        body: JSON.stringify({ community_data_opt_out: newVal }),
      });
      setOptOut(newVal);
    } catch {
      // Revert on error
    } finally {
      setSavingOptOut(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------
  const insufficientPathways = new Set(freeTierData?.insufficient_pathways || []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-[#3A3A3F] text-white">
      {/* Header */}
      <div className="bg-gray-950 border-b border-gold/20 px-6 py-4 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <i className="fas fa-arrow-left mr-2" />
              Dashboard
            </button>
            <span className="text-gray-600">|</span>
            <h1 className="text-white font-semibold text-lg">
              <i className="fas fa-users text-gold mr-2" />
              Community Data
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {isFullTier ? (
              <span className="inline-flex items-center gap-1 bg-gold/20 text-gold text-xs font-semibold px-3 py-1 rounded-full border border-gold/30">
                <i className="fas fa-star" /> Full Access
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 bg-gray-800 text-gray-400 text-xs font-semibold px-3 py-1 rounded-full border border-gray-700">
                <i className="fas fa-lock-open" /> Free Tier
              </span>
            )}
            <PurchaseNavButton />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8">

        {/* Subtitle */}
        <p className="text-gray-500 text-sm">
          Aggregated anonymised case outcomes from ImmigrateSmart users
        </p>
        <ComingSoonBanner dark>
          Community data is visible in beta. It is not part of the public launch product yet.
        </ComingSoonBanner>

        {/* ── FREE TIER ──────────────────────────────────────────────────── */}
        <section>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="text-lg font-semibold text-white">
              <i className="fas fa-chart-bar text-gold mr-2" />
              Community Overview
              <span className="ml-2 text-xs text-gray-500 font-normal">(90-day snapshot)</span>
            </h2>
            {/* Sub-tab nav */}
            <div className="flex gap-2">
              {[
                { id: 'approvals', label: 'Approval Rates' },
                { id: 'processing', label: 'Processing Times' },
                { id: 'rfe', label: 'RFE Reasons' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    activeTab === tab.id
                      ? 'bg-gold text-white border-gold'
                      : 'border-gray-700 text-gray-400 hover:text-white hover:border-gray-500'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {loadingFree ? (
            <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
              <i className="fas fa-spinner fa-spin" /> Loading community stats…
            </div>
          ) : errorFree ? (
            <div className="bg-red-900/30 border border-red-500/40 rounded-lg p-4 text-red-300 text-sm">
              <i className="fas fa-exclamation-triangle mr-2" />{errorFree}
              <button onClick={fetchFreeTier} className="ml-3 underline text-red-400 text-xs">Retry</button>
            </div>
          ) : freeTierData ? (
            <>
              {/* Approval Rates */}
              {activeTab === 'approvals' && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {ALL_PATHWAYS.map((pid) => (
                    <PathwayCard
                      key={pid}
                      pathwayId={pid}
                      approvalRate={freeTierData.approval_rates?.[pid]}
                      avgDays={freeTierData.avg_processing_times?.[pid]}
                      recordCount={freeTierData.record_counts?.[pid] || 0}
                      insufficient={insufficientPathways.has(pid)}
                    />
                  ))}
                </div>
              )}

              {/* Processing Times */}
              {activeTab === 'processing' && (
                <div className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-gray-400 font-semibold">Pathway</th>
                        <th className="px-4 py-3 text-right text-gray-400 font-semibold">Avg Processing</th>
                        <th className="px-4 py-3 text-right text-gray-400 font-semibold">Cases on File</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ALL_PATHWAYS.map((pid, i) => (
                        <tr key={pid} className={i % 2 === 0 ? 'bg-gray-900' : 'bg-gray-900/50'}>
                          <td className="px-4 py-2.5 font-medium text-white">{PATHWAY_LABELS[pid]}</td>
                          <td className="px-4 py-2.5 text-right text-gray-300">
                            {insufficientPathways.has(pid) ? (
                              <span className="text-gray-600 italic text-xs">Insufficient data</span>
                            ) : (
                              fmtDays(freeTierData.avg_processing_times?.[pid])
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-500 text-xs">
                            {freeTierData.record_counts?.[pid] || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* RFE Reasons */}
              {activeTab === 'rfe' && (
                <RFEReasonsSection topRfeReasons={freeTierData.top_rfe_reasons} />
              )}

              {LAUNCH.showPolicyAlerts && activeTab === 'policy' && (
                <div className="space-y-3">
                  {(freeTierData.recent_policy_changes || []).length === 0 ? (
                    <p className="text-gray-500 text-sm italic">No recent policy changes on file.</p>
                  ) : (
                    (freeTierData.recent_policy_changes || []).map((change, i) => (
                      <PolicyAlertCard
                        key={i}
                        alert={{ ...change, read: true }}
                        onMarkRead={() => {}}
                      />
                    ))
                  )}
                </div>
              )}

              {/* Generated-at footer */}
              <p className="text-xs text-gray-600 mt-3">
                Snapshot generated: {freeTierData.generated_at?.slice(0, 10) || '—'}
                &nbsp;· expires: {freeTierData.expires_at?.slice(0, 10) || '—'}
              </p>
            </>
          ) : null}
        </section>

        {/* ── FULL TIER ──────────────────────────────────────────────────── */}
        {isFullTier ? (
          <section>
            <h2 className="text-lg font-semibold text-white mb-4">
              <i className="fas fa-star text-gold mr-2" />
              Real-Time Pathway Analysis
              <span className="ml-2 text-xs text-gray-500 font-normal">(updated weekly, cached 24h)</span>
            </h2>

            {/* Pathway + filter selectors */}
            <div className="flex flex-wrap gap-3 mb-5">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Pathway</label>
                <select
                  value={selectedPathway}
                  onChange={(e) => { setSelectedPathway(e.target.value); setFullTierData(null); }}
                  className="border border-gray-700 rounded-lg px-3 py-2 text-sm text-white bg-gray-900 focus:border-gold focus:ring-1 focus:ring-gold"
                >
                  {ALL_PATHWAYS.map((pid) => (
                    <option key={pid} value={pid}>{SHORT_LABELS[pid] || pid}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Service Center</label>
                <select
                  value={selectedSC}
                  onChange={(e) => { setSelectedSC(e.target.value); setFullTierData(null); }}
                  className="border border-gray-700 rounded-lg px-3 py-2 text-sm text-white bg-gray-900 focus:border-gold focus:ring-1 focus:ring-gold"
                >
                  <option value="">All Centers</option>
                  {SERVICE_CENTERS.map((sc) => (
                    <option key={sc} value={sc}>{sc}</option>
                  ))}
                </select>
              </div>
              {EB_PATHWAYS.has(selectedPathway) && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Country of Birth</label>
                  <select
                    value={selectedCountry}
                    onChange={(e) => { setSelectedCountry(e.target.value); setFullTierData(null); }}
                    className="border border-gray-700 rounded-lg px-3 py-2 text-sm text-white bg-gray-900 focus:border-gold focus:ring-1 focus:ring-gold"
                  >
                    <option value="">All Countries</option>
                    {['INDIA', 'CHINA', 'MEXICO', 'OTHER'].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="self-end">
                <button
                  onClick={fetchFullTier}
                  disabled={loadingFull}
                  className="bg-gold text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gold/80 disabled:opacity-50 transition-colors"
                >
                  {loadingFull ? <i className="fas fa-spinner fa-spin mr-1" /> : <i className="fas fa-search mr-1" />}
                  Apply
                </button>
              </div>
            </div>

            {loadingFull && (
              <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
                <i className="fas fa-spinner fa-spin" /> Loading detailed stats…
              </div>
            )}

            {errorFull && (
              <div className="bg-red-900/30 border border-red-500/40 rounded-lg p-4 text-red-300 text-sm">
                <i className="fas fa-exclamation-triangle mr-2" />{errorFull}
              </div>
            )}

            {!loadingFull && fullTierData && (
              fullTierData.insufficient_data ? (
                <div className="bg-gray-900 border border-gold/30 rounded-lg p-5 text-center">
                  <i className="fas fa-info-circle text-gold text-xl mb-2" />
                  <p className="text-white font-medium">Insufficient data</p>
                  <p className="text-gray-400 text-sm mt-1">
                    Only {fullTierData.record_count} case{fullTierData.record_count !== 1 ? 's' : ''} on file
                    for this filter combination. A minimum of 50 cases is required.
                  </p>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Headline stats */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'Approval Rate', value: fmtPct(fullTierData.approval_rate), color: 'text-green-400' },
                      { label: 'Denial Rate', value: fmtPct(fullTierData.denial_rate), color: 'text-red-400' },
                      { label: 'RFE Rate', value: fmtPct(fullTierData.rfe_rate), color: 'text-yellow-400' },
                      { label: 'Avg Processing', value: fmtDays(fullTierData.avg_processing_time_days), color: 'text-blue-400' },
                    ].map((stat) => (
                      <div key={stat.label} className="bg-gray-900 border border-gray-700 rounded-lg p-4 text-center">
                        <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
                        <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* 12-month trend */}
                  {fullTierData.monthly_trend?.length > 0 && (
                    <div className="bg-gray-900 border border-gray-700 rounded-lg p-5">
                      <h3 className="font-semibold text-white text-sm mb-3">
                        <i className="fas fa-chart-line mr-1 text-gold" /> 12-Month Approval Trend
                      </h3>
                      <ApprovalTrendChart trend={fullTierData.monthly_trend} />
                    </div>
                  )}

                  {/* Service center breakdown */}
                  {Object.keys(fullTierData.by_service_center || {}).length > 0 && (
                    <div className="bg-gray-900 border border-gray-700 rounded-lg p-5">
                      <h3 className="font-semibold text-white text-sm mb-3">
                        <i className="fas fa-building mr-1 text-purple-400" /> By Service Center
                      </h3>
                      <div className="space-y-2">
                        {Object.entries(fullTierData.by_service_center).map(([sc, sc_data]) => (
                          <div key={sc} className="flex items-center gap-3 text-sm">
                            <span className="text-gray-400 w-28 shrink-0">{sc}</span>
                            <div className="flex-1 bg-gray-800 rounded h-5 relative overflow-hidden">
                              <div
                                className="h-full rounded bg-purple-500/70"
                                style={{ width: `${Math.round((sc_data.approval_rate || 0) * 100)}%` }}
                              />
                              <span className="absolute inset-0 flex items-center px-2 text-white text-xs font-medium">
                                {fmtPct(sc_data.approval_rate)} ({sc_data.count} cases)
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Evidence category frequency */}
                  {Object.keys(fullTierData.evidence_category_frequency || {}).length > 0 && (
                    <div className="bg-gray-900 border border-gray-700 rounded-lg p-5">
                      <h3 className="font-semibold text-white text-sm mb-3">
                        <i className="fas fa-folder-open mr-1 text-orange-400" /> Evidence Category Frequency
                      </h3>
                      <div className="space-y-1">
                        {Object.entries(fullTierData.evidence_category_frequency)
                          .sort((a, b) => b[1] - a[1])
                          .map(([cat, freq]) => (
                            <div key={cat} className="flex items-center gap-3 text-sm">
                              <span className="text-gray-400 w-40 shrink-0 truncate">{cat}</span>
                              <div className="flex-1 bg-gray-800 rounded h-4 relative overflow-hidden">
                                <div
                                  className="h-full rounded bg-orange-500/70"
                                  style={{ width: `${Math.round(freq * 100)}%` }}
                                />
                              </div>
                              <span className="text-gray-400 text-xs w-10 text-right">{fmtPct(freq)}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Country breakdown */}
                  {fullTierData.country_breakdown && Object.keys(fullTierData.country_breakdown).length > 0 && (
                    <div className="bg-gray-900 border border-gray-700 rounded-lg p-5">
                      <h3 className="font-semibold text-white text-sm mb-3">
                        <i className="fas fa-globe mr-1 text-teal-400" /> Country of Birth Distribution
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {Object.entries(fullTierData.country_breakdown)
                          .sort((a, b) => b[1] - a[1])
                          .map(([country, count]) => (
                            <div key={country} className="bg-gray-800 border border-gray-700 rounded-lg p-3 text-center">
                              <div className="text-xl font-bold text-teal-400">{count}</div>
                              <div className="text-xs text-gray-500 mt-1">{country}</div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-gray-600">
                    Data as of: {fullTierData.data_as_of?.slice(0, 10) || '—'}
                    &nbsp;· {fullTierData.record_count} total cases
                  </p>
                </div>
              )
            )}
          </section>
        ) : (
          /* Upgrade banner for free-tier users */
          <div className="bg-gray-900 border border-gold/30 rounded-xl p-6 text-center">
            <i className="fas fa-star text-gold text-2xl mb-2" />
            <h3 className="font-bold text-white text-lg">Unlock Full Community Data</h3>
            <p className="text-gray-400 text-sm mt-2 max-w-lg mx-auto">
              Purchase any ImmigrateSmart pathway to access real-time approval rates by service center,
              evidence package breakdowns, 12-month trends, and country-of-birth analysis.
            </p>
            <a
              href="/pricing"
              className="inline-block mt-4 bg-gold text-white px-6 py-2 rounded-full text-sm font-semibold hover:bg-gold/80 transition-colors"
            >
              View Pathways
            </a>
          </div>
        )}

        {LAUNCH.showPolicyAlerts && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-lg font-semibold text-white">
              <i className="fas fa-bell text-gold mr-2" />
              Your Policy Alerts
            </h2>
            {unreadCount > 0 && (
              <span className="bg-gold text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {unreadCount} unread
              </span>
            )}
          </div>

          {loadingAlerts ? (
            <p className="text-gray-500 text-sm"><i className="fas fa-spinner fa-spin mr-1" /> Loading alerts…</p>
          ) : policyAlerts.length === 0 ? (
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-5 text-center">
              <i className="fas fa-bell-slash text-gray-600 text-2xl mb-2" />
              <p className="text-gray-500 text-sm">No policy alerts for your pathways yet.</p>
              <p className="text-gray-600 text-xs mt-1">
                Alerts are generated when USCIS policies change for your active pathways.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {policyAlerts.map((alert) => (
                <PolicyAlertCard key={alert.id} alert={alert} onMarkRead={handleMarkRead} />
              ))}
            </div>
          )}
        </section>
        )}

        {/* ── OPT-OUT SETTINGS ──────────────────────────────────────────── */}
        <section className="bg-gray-900 border border-gray-700 rounded-xl p-5">
          <h2 className="text-base font-semibold text-white mb-1">
            <i className="fas fa-shield-alt text-gray-500 mr-2" />
            Data Contribution Settings
          </h2>
          <p className="text-gray-500 text-sm mb-4">
            ImmigrateSmart automatically contributes anonymised case outcome data (pathway type,
            service center, filing date, outcome) to improve community statistics. No personal
            information, employer names, school names, or A-numbers are ever stored.
          </p>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white">
                {optOut ? 'You are opted out of data contribution' : 'Contributing anonymised data'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {optOut
                  ? 'Your cases will not be included in community statistics.'
                  : 'Your anonymised case outcomes help the community. Opting out does not affect your access to community data.'}
              </p>
            </div>
            <button
              onClick={handleOptOutToggle}
              disabled={savingOptOut}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                optOut ? 'bg-gray-700' : 'bg-gold'
              } ${savingOptOut ? 'opacity-50 cursor-not-allowed' : ''}`}
              aria-label="Toggle opt-out"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  optOut ? 'translate-x-1' : 'translate-x-6'
                }`}
              />
            </button>
          </div>
        </section>

      </div>
    </div>
  );
}
